/**
 * CJAS — Ramadan mode.  Jira CA-56; the Ramadan module (owner 2026-10-31, spec 09).
 *
 * Sehri ends at Fajr begins; iftar is at Maghrib. Both come straight from the
 * uploaded timetable — there is no calculation engine anywhere in this
 * platform (CA-20), and Ramadan is not where we start adding one.
 *
 * The interesting problems here are not astronomical, they are human:
 * someone eating at 03:40 wants a countdown that is obviously trustworthy,
 * and the mode must switch itself on and off without an admin remembering.
 *
 * The mosque's own choices (2026-10-31, `ramadan_settings`, one row) are passed IN to every function here — this
 * module reads no database and no clock:
 *   imsakOffsetMin     sehri ends N minutes BEFORE Fajr begins (0–30; 0 = at Fajr begins, as before);
 *   taraweehMode       'after_isha' (N minutes after the Isha jamaat) or 'fixed' (a clock time the imam announced);
 *   taraweehAfterMin   the N (default 20);  taraweehFixedTime  'HH:MM' for 'fixed';
 *   iftarNote / sehriNote   one line each for the app («Iftar serveres i kjelleren»), ≤ 200;
 *   showIftarSignup / showSehriSignup   whether the app offers «Meld på»;  iftarCapacity / sehriCapacity  seats per day.
 * Without settings (an older server, the phone offline) everything behaves exactly as it did before this row existed.
 */

const TARAWEEH_DEFAULT_AFTER_ISHA_MINUTES = 20;
const TARAWEEH_MODES = ['after_isha', 'fixed'];
const MAX_IMSAK_OFFSET_MIN = 30;
const MAX_TARAWEEH_AFTER_MIN = 180;
const MAX_NOTE = 200;
const MAX_CAPACITY = 10000;
/** The party sizes the app offers on «Meld på iftar» (staff may register bigger parties in the panel). */
const APP_MAX_PARTY = 6;
/** The lists a Ramadan day can have, in the order the app shows them. */
const SIGNUP_KINDS = ['iftar', 'sehri'];
const KIND_NO = { iftar: 'Iftar', sehri: 'Sehri' };

const DEFAULT_SETTINGS = Object.freeze({
  imsakOffsetMin: 0, taraweehMode: 'after_isha', taraweehAfterMin: TARAWEEH_DEFAULT_AFTER_ISHA_MINUTES, taraweehFixedTime: null,
  iftarNote: null, sehriNote: null, showIftarSignup: false, showSehriSignup: false, iftarCapacity: null, sehriCapacity: null,
});
const SETTINGS_FIELDS = Object.keys(DEFAULT_SETTINGS);

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

function at(dateStr, hhmm) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const [h, min] = String(hhmm).split(':').map(Number);
  return new Date(y, m - 1, d, h, min, 0, 0);
}
const hhmmOf = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

const iso = d => {
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** 'HH:MM' minus n minutes, as 'HH:MM' (wraps within the day; a sehri 30 minutes before a 00:10 Fajr is 23:40). */
function minusMinutes(hhmm, n) {
  if (!HHMM.test(String(hhmm || ''))) return null;
  const [h, m] = String(hhmm).split(':').map(Number);
  const t = ((h * 60 + m - (Number(n) || 0)) % 1440 + 1440) % 1440;
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
}

/** The settings as every function here reads them: the row's values over the defaults; a string = a legacy announced taraweeh. */
function settingsOf(s) {
  if (!s || typeof s !== 'object') return { ...DEFAULT_SETTINGS, confirmed: false };
  const out = { ...DEFAULT_SETTINGS };
  for (const k of SETTINGS_FIELDS) if (s[k] !== undefined && s[k] !== null) out[k] = s[k];
  if (s.taraweehFixedTime === null) out.taraweehFixedTime = null;
  for (const k of ['iftarNote', 'sehriNote', 'iftarCapacity', 'sehriCapacity']) if (s[k] === null) out[k] = null;
  out.confirmed = Boolean(s.confirmed);
  return out;
}

/**
 * The settings, new or changed, as the panel sends them. → { ok, value, changed } | { ok:false, errors }.
 * `base`: the current row (camelCase) or null. Unknown fields are refused; a fixed taraweeh needs its time.
 */
function settingsInput(input, base = null) {
  const fail = (field, message) => ({ ok: false, errors: { [field]: message } });
  if (!input || typeof input !== 'object' || Array.isArray(input)) return fail('settings', 'Ugyldige innstillinger.');
  const bad = Object.keys(input).filter((k) => !SETTINGS_FIELDS.includes(k));
  if (bad.length) return fail(bad[0], 'Ukjent felt.');
  const b = settingsOf(base);
  const out = {};
  for (const k of SETTINGS_FIELDS) out[k] = b[k];
  const int = (k, label, min, max, nullable) => {
    if (input[k] === undefined) return null;
    if (input[k] === null) { if (!nullable) return fail(k, `${label} må fylles ut.`); out[k] = null; return null; }
    if (!Number.isSafeInteger(input[k]) || input[k] < min || input[k] > max) return fail(k, `${label} må være ${min}–${max}.`);
    out[k] = input[k]; return null;
  };
  const note = (k, label) => {
    if (input[k] === undefined) return null;
    if (input[k] === null || input[k] === '') { out[k] = null; return null; }
    if (typeof input[k] !== 'string') return fail(k, `${label} må være tekst.`);
    const s = input[k].replace(/\s+/g, ' ').trim();
    if (s.length > MAX_NOTE) return fail(k, `${label} kan ha maks ${MAX_NOTE} tegn.`);
    if (/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(s)) return fail(k, `${label} kan ikke ha kontrolltegn.`);
    out[k] = s || null; return null;
  };
  const flag = (k, label) => {
    if (input[k] === undefined) return null;
    if (typeof input[k] !== 'boolean') return fail(k, `${label} må være på eller av.`);
    out[k] = input[k]; return null;
  };
  let r;
  if ((r = int('imsakOffsetMin', 'Imsak', 0, MAX_IMSAK_OFFSET_MIN, false))) return r;
  if (input.taraweehMode !== undefined) {
    if (!TARAWEEH_MODES.includes(input.taraweehMode)) return fail('taraweehMode', 'Velg «etter isha» eller «fast klokkeslett».');
    out.taraweehMode = input.taraweehMode;
  }
  if ((r = int('taraweehAfterMin', 'Minutter etter isha', 0, MAX_TARAWEEH_AFTER_MIN, false))) return r;
  if (input.taraweehFixedTime !== undefined) {
    if (input.taraweehFixedTime === null || input.taraweehFixedTime === '') out.taraweehFixedTime = null;
    else if (!HHMM.test(String(input.taraweehFixedTime))) return fail('taraweehFixedTime', 'Klokkeslettet må være TT:MM.');
    else out.taraweehFixedTime = String(input.taraweehFixedTime);
  }
  if (out.taraweehMode === 'fixed' && !out.taraweehFixedTime) return fail('taraweehFixedTime', 'Skriv klokkeslettet for taraweeh.');
  if ((r = note('iftarNote', 'Iftar-notatet'))) return r;
  if ((r = note('sehriNote', 'Sehri-notatet'))) return r;
  if ((r = flag('showIftarSignup', 'Påmelding til iftar'))) return r;
  if ((r = flag('showSehriSignup', 'Påmelding til sehri'))) return r;
  if ((r = int('iftarCapacity', 'Plasser til iftar', 1, MAX_CAPACITY, true))) return r;
  if ((r = int('sehriCapacity', 'Plasser til sehri', 1, MAX_CAPACITY, true))) return r;
  const changed = base ? SETTINGS_FIELDS.filter((k) => (b[k] ?? null) !== (out[k] ?? null)) : SETTINGS_FIELDS.slice();
  return { ok: true, value: out, changed };
}

/**
 * Ramadan mode turns itself on from the Hijri month, not from a switch an
 * admin has to remember to flip on the right evening — and not from a date
 * range typed in once and wrong the following year.
 */
function isRamadan(hijriDate) {
  return Boolean(hijriDate) && Number(hijriDate.month) === 9;
}

/**
 * Sehri and iftar for a day.
 *
 * Sehri ends when Fajr BEGINS, never at the jamaat time. Fajr jamaat can be
 * forty minutes after Fajr begins, and a member who trusts the jamaat time is
 * eating well into the fast. This is the single most dangerous confusion in
 * the whole feature. With an imsak offset the mosque ends sehri N minutes
 * EARLIER still (`sehriEndsLabel` is then the imsak; `fajrLabel` stays Fajr begins).
 */
function timesFor(day, settings = null) {
  if (!day || !day.begins || !day.begins.fajr || !day.begins.maghrib) return null;
  const s = settingsOf(settings);
  const imsak = s.imsakOffsetMin > 0 ? minusMinutes(day.begins.fajr, s.imsakOffsetMin) : day.begins.fajr;
  const sehriEnds = new Date(at(day.date, day.begins.fajr).getTime() - s.imsakOffsetMin * 60000);
  return {
    date: day.date,
    sehriEnds,
    sehriEndsLabel: imsak,
    imsakOffsetMin: s.imsakOffsetMin,
    fajrLabel: day.begins.fajr,
    iftar: at(day.date, day.begins.maghrib),
    iftarLabel: day.begins.maghrib,
    source: 'timetable',
  };
}

/**
 * Taraweeh. Held at the mosque, so this is an announced time, not a derived
 * one — but a sensible default keeps the screen from being empty while the
 * imam decides. `settings` may still be a bare 'HH:MM' (the old announced time).
 *   fixed mode (or an announced time)  → source 'announced';
 *   after_isha                          → source 'default', `provisional` until the mosque has saved its settings once.
 */
function taraweehFor(day, settings = null) {
  if (typeof settings === 'string' && settings) return { at: at(day.date, settings), time: settings, source: 'announced' };
  const s = settingsOf(settings);
  if (s.taraweehMode === 'fixed' && s.taraweehFixedTime) return { at: at(day.date, s.taraweehFixedTime), time: s.taraweehFixedTime, source: 'announced' };
  const isha = day && day.jamaat && day.jamaat.isha;
  if (!isha) return null;
  const t = new Date(at(day.date, isha).getTime() + s.taraweehAfterMin * 60000);
  return { at: t, time: hhmmOf(t), source: 'default', afterIshaMin: s.taraweehAfterMin, provisional: !s.confirmed };
}

/**
 * The Ramadan home screen: which countdown, to what, right now.
 *
 * Before sehri ends we count to it. Between that and Maghrib we count to
 * iftar. After iftar we count to TOMORROW's sehri — using tomorrow's time,
 * the same rule as the ordinary next-jamaat rollover.
 */
function countdown(days, now = new Date(), settings = null) {
  const today = (days || []).find(d => d.date === iso(now));
  if (!today) return { state: 'no_data' };
  const t = timesFor(today, settings);
  if (!t) return { state: 'no_data' };

  if (now < t.sehriEnds) {
    return { state: 'before_sehri', target: t.sehriEnds, label: 'sehri_ends',
             time: t.sehriEndsLabel, secondsLeft: Math.floor((t.sehriEnds - now) / 1000) };
  }
  if (now < t.iftar) {
    return { state: 'fasting', target: t.iftar, label: 'iftar',
             time: t.iftarLabel, secondsLeft: Math.floor((t.iftar - now) / 1000) };
  }

  // Tomorrow by the CALENDAR, not now + 24 h. On the night the clocks go
  // forward, 23:30 + 24 h is 00:30 the day after tomorrow, and the countdown
  // would look for the wrong sehri. Ramadan 1446 ended on exactly that night
  // (30 March 2025); it will again. See dates.js — calendar days are not
  // elapsed milliseconds.
  const tomorrowStr = iso(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1));
  const tomorrow = (days || []).find(d => d.date === tomorrowStr);
  const tt = tomorrow && timesFor(tomorrow, settings);
  if (!tt) return { state: 'after_iftar', target: null, label: 'iftar_done' };
  return { state: 'after_iftar', target: tt.sehriEnds, label: 'sehri_ends',
           time: tt.sehriEndsLabel, secondsLeft: Math.floor((tt.sehriEnds - now) / 1000) };
}

/**
 * The last ten nights, and whether tonight is one of the odd nights. Not
 * decoration — attendance triples and the mosque plans staffing around it.
 */
function lastTenNights(hijriDate) {
  if (!isRamadan(hijriDate)) return null;
  const day = Number(hijriDate.day);
  if (day < 21) return { active: false, startsInDays: 21 - day };
  return { active: true, night: day, isOdd: day % 2 === 1,
           laylatAlQadrCandidate: [21, 23, 25, 27, 29].includes(day) };
}

/**
 * Everything the Ramadan tab, the home card, the TV slide and the printout need for one day. Returns null outside
 * Ramadan so the caller cannot accidentally show a sehri countdown in August. `settings` as settingsOf() reads them
 * (or a bare announced taraweeh 'HH:MM', as before).
 */
function dayView(day, hijriDate, settings = null, now = new Date()) {
  if (!isRamadan(hijriDate)) return null;
  const t = timesFor(day, settings);
  if (!t) return null;
  const s = typeof settings === 'string' ? settingsOf(null) : settingsOf(settings);
  return {
    ...t,
    hijriDay: Number(hijriDate.day),
    hijriYear: hijriDate.year ? Number(hijriDate.year) : null,
    fastLengthMinutes: Math.round((t.iftar - t.sehriEnds) / 60000),
    taraweeh: taraweehFor(day, settings),
    iftarNote: s.iftarNote, sehriNote: s.sehriNote,
    signup: { iftar: Boolean(s.showIftarSignup), sehri: Boolean(s.showSehriSignup) },
    lastTen: lastTenNights(hijriDate),
    countdown: countdown([day], now, settings),
  };
}

/**
 * «Neste ramadan starter ca. <date>»: the first of Ramadan on or after `from` (a 'YYYY-MM-DD' or a Date), by the
 * tabular calendar with the mosque's offset (islamic-dates). Searches 400 days: two Ramadans never are further apart.
 * → { date, inDays, hijriYear } or null. "ca." is the app's word: the mosque announces the real night by sighting.
 */
function nextStart(from, offset = 0) {
  const D = require('./islamic-dates');
  const hit = D.upcoming(from, 400, { offset: Number(offset) || 0 }).find((x) => x.key === 'ramadanStart');
  return hit ? { date: hit.date, inDays: hit.inDays, hijriYear: hit.hijri ? hit.hijri.year : null } : null;
}

/** The sign-up kinds the settings switch on, in the app's order. */
function signupKinds(settings) {
  const s = settingsOf(settings);
  return SIGNUP_KINDS.filter((k) => (k === 'iftar' ? s.showIftarSignup : s.showSehriSignup));
}

/**
 * What one Ramadan day's list of `kind` is made with («Opprett lister for ramadan»): the capacity from the settings
 * (null = no limit) and the waiting list on whenever there is a limit — event-ops' own rules then place every party.
 */
function listSpec(kind, settings) {
  const s = settingsOf(settings);
  const capacity = kind === 'iftar' ? s.iftarCapacity : s.sehriCapacity;
  return { kind, capacity: capacity ?? null, waitlist: capacity !== null && capacity !== undefined };
}

/**
 * A member's own sign-up from the app: the party size only (the name comes from the account). → { ok, value } |
 * { ok:false, errors }. 1–APP_MAX_PARTY: a family, not a coach.
 */
function appSignupInput(input) {
  const fail = (field, message) => ({ ok: false, errors: { [field]: message } });
  if (!input || typeof input !== 'object' || Array.isArray(input)) return fail('signup', 'Ugyldig påmelding.');
  const bad = Object.keys(input).filter((k) => k !== 'partySize');
  if (bad.length) return fail(bad[0], 'Ukjent felt.');
  const size = input.partySize === undefined ? 1 : input.partySize;
  if (!Number.isSafeInteger(size) || size < 1 || size > APP_MAX_PARTY) return fail('partySize', `Antall må være 1–${APP_MAX_PARTY}.`);
  return { ok: true, value: { partySize: size } };
}

/** Audit fields (packages/core/audit.js RAMADAN_FIELDS): the choices, never a note. */
const settingsAudit = (s) => {
  const v = settingsOf(s);
  return { ramadan_imsak_offset_min: v.imsakOffsetMin, ramadan_taraweeh_mode: v.taraweehMode, ramadan_taraweeh_after_min: v.taraweehAfterMin,
    ramadan_taraweeh_fixed_time: v.taraweehFixedTime, ramadan_show_iftar_signup: v.showIftarSignup, ramadan_show_sehri_signup: v.showSehriSignup,
    ramadan_iftar_capacity: v.iftarCapacity, ramadan_sehri_capacity: v.sehriCapacity };
};

module.exports = {
  TARAWEEH_DEFAULT_AFTER_ISHA_MINUTES, TARAWEEH_MODES, MAX_IMSAK_OFFSET_MIN, MAX_TARAWEEH_AFTER_MIN, MAX_NOTE, MAX_CAPACITY,
  APP_MAX_PARTY, SIGNUP_KINDS, KIND_NO, DEFAULT_SETTINGS, SETTINGS_FIELDS,
  minusMinutes, settingsOf, settingsInput, isRamadan, timesFor, taraweehFor, countdown, lastTenNights, dayView, nextStart,
  signupKinds, listSpec, appSignupInput, settingsAudit,
};
